<template>
  <Modal :y="50" :yTransform="50">
    <div class="flex justify-end">
      <div @click="$emit('close-modal')" class="clickable circular p-2">
        <CloseIcon />
      </div>
    </div>
    <div class="flex justify-center">
      <h1 class="text-3xl">Manage Users</h1>
    </div>
    <div>
      <p class="text-md">Add User</p>
      <Table
        class="styled-table"
        :headers="[
          'First Name',
          'Last Name',
          {
            header: 'Username',
            info: 'Must match the IdP username or email (NameID). If it does not match REDCap login, you may not be able to create TLFB surveys or edit projects.'
          },
          'Is Admin',
          'Actions'
        ]"
      >
        <EditUserTableRow
          @clear="clearNewUser"
          @refresh-users="getUsers"
          :user="newUser"
        />
      </Table>
      <p class="text-md" style="margin-top: 2rem">Edit Users</p>
      <Table
        v-if="loaded"
        class="styled-table relative w-full"
        :headers="[
          'First Name',
          'Last Name',
          'Username',
          'Is Admin',
          'Actions'
        ]"
      >
        <template v-for="u in users" :key="'description-' + u.username">
          <EditUserTableRow
            v-if="userBeingEdited === u"
            @edit-cancelled="userBeingEdited = null"
            @refresh-users="getUsers()"
            :user="u"
            :isOnlyAdmin="onlyOneRemainingAdmin && u.isAdmin"
          />
          <TableRow v-else>
            <Cell class="w-56"> {{ u.firstName }} </Cell>
            <Cell class="w-56"> {{ u.lastName }} </Cell>
            <Cell class="w-56"> {{ u.username }} </Cell>
            <Cell>{{ u.isAdmin ? "Yes" : "No" }}</Cell>
            <Cell>
              <div class="flex justify-start">
                <button
                  @click="userBeingEdited = u"
                  :disabled="disableEditButton()"
                  :class="[
                    'user-action p-1 pl-2 pr-2',
                    disableEditButton() ? 'disabled' : 'clickable'
                  ]"
                >
                  Edit
                </button>
                <button
                  @click="deleteUser(u)"
                  :disabled="disableEditButton()"
                  :class="[
                    'user-action p-1 pl-2 pr-2',
                    disableEditButton() ? 'disabled' : 'clickable'
                  ]"
                >
                  Delete
                </button>
              </div>
            </Cell>
          </TableRow>
        </template>
      </Table>
      <Text :class="{ hidden: !error }">{{ errorMessage }}</Text>
    </div>
  </Modal>
</template>

<script setup>
import { onMounted, ref, computed } from "vue"
import { UserApi } from "../api"
import EditUserTableRow from "./EditUserTableRow.vue"
import TableRow from "./TableRow.vue"
import CloseIcon from "./CloseIcon.vue"
import InfoIcon from "./InfoIcon.vue"
import Modal from "./Modal.vue"
import Table from "./Table.vue"
import Cell from "./Cell.vue"
import Text from "./Text.vue"

defineEmits(["close-modal"])

const props = defineProps(["user"])

const users = ref([])
const loaded = ref(false)

function initUser() {
  return {
    username: "",
    firstName: "",
    lastName: "",
    isAdmin: false
  }
}

const newUser = ref(initUser())
const errorMessage = ref('')
const userBeingEdited = ref(null)

function disableEditButton() {
  /* Admin users can edit/delete other users */
  return !props.user.isAdmin
}

onMounted(async () => getUsers())

async function getUsers() {
  try {
    const response = await UserApi.all()
    if (response.status === 200) {
      users.value = response.data
      loaded.value = true
    }
  } catch (error) {
    console.error("error: ", error.response)
  }
}

const onlyOneRemainingAdmin = computed(() => {
  return users.value.filter(u => u.isAdmin).length === 1
})

async function deleteUser(user) {
  errorMessage.value = ''

  if (users.value.length == 1) {
    errorMessage.value = "Cannot delete only remaining user."
  } else if (onlyOneRemainingAdmin.value && user.isAdmin) {
    errorMessage.value = "Cannot delete only remaining Admin user."
  } else {
    const isConfirmed = confirm(
      `Remove user: ${user.firstName} ${user.lastName}?`
    )
    if (isConfirmed) {
      try {
        const response = await UserApi.destroy(user.id)
        if (response.status === 200) {
          getUsers()
        }
      } catch (error) {
        console.log("error: ", error.response)
        errorMessage.value = 'Cannot delete user: Ensure that the user is unsynced from all REDCap projects.'
      }
    }
  }
}

function clearNewUser() {
  newUser.value = initUser()
}
</script>

<style scoped>
.add-user,
.remove-user {
  background-color: #740000;
  display: inline-block;
  font-family: inherit;
  text-align: center;
  border-radius: 4px;
  font-weight: bold;
  padding: 0 10px;
  font-size: 18px;
  height: 2.5rem;
  color: white;
  margin: 10px;
  width: auto;
}

.add-user:hover,
.remove-user:hover {
  background-color: #b53e3e;
  text-decoration: underline;
}

.user-edit-options {
  font-size: 18px;
  font-weight: bold;
}

.user-action {
  border: 1px solid rgb(156, 163, 175);
  margin-right: 10px;
  background: white;
  border-radius: 5px;
  outline: none;
}

.action:hover {
  color: #530000;
}

th {
  background-color: #740000;
  color: #ffffff;
  text-align: left;
}
</style>
